import { useState } from 'react';
import { useAuthorization } from 'framework/authorization';
import { AddressableViewLayout } from 'framework/ViewTypes';
import { FilterCondition } from 'framework/types';
import { bindCollection } from 'framework/ViewTypes/contentLayout';
import { RECRUITMENT_POOL_CREATE, RECRUITMENT_POOL_STATUS_LABELS, RECRUITMENT_POOL_UPDATE } from '../constants';
import { RecruitmentPoolFormDrawer } from '../createupdate';
import { useRecruitmentPoolUrl } from '../routing';
import type { RecruitmentPool, RecruitmentPoolStatus } from '../types';
import useRecruitmentPoolService from '../useRecruitmentPoolService';
import { getRecruitmentPoolsTableConfig } from './recruitmentPoolsTableConfig';
import useRecruitmentPoolsCollection from './useRecruitmentPoolsCollection';

const RecruitmentPoolsListPage = () => {
  const { isGranted } = useAuthorization();
  const { getSingleEntityUrl } = useRecruitmentPoolUrl();
  const collection = useRecruitmentPoolsCollection();
  const { transit } = useRecruitmentPoolService();
  const [poolToEdit, setPoolToEdit] = useState<RecruitmentPool>();
  const [isFormDrawerOpen, setFormDrawerOpen] = useState(false);

  const openCreateDrawer = () => {
    setPoolToEdit(undefined);
    setFormDrawerOpen(true);
  };

  const openEditDrawer = (pool: RecruitmentPool) => {
    setPoolToEdit(pool);
    setFormDrawerOpen(true);
  };

  const changeStatus = async (pool: RecruitmentPool, status: RecruitmentPoolStatus) => {
    await transit(pool.id, status);
    await collection.reload();
  };

  const binding = bindCollection(
    collection,
    getRecruitmentPoolsTableConfig(collection.rows, getSingleEntityUrl, {
      canCreate: isGranted(RECRUITMENT_POOL_CREATE),
      canUpdate: isGranted(RECRUITMENT_POOL_UPDATE),
      onCreate: openCreateDrawer,
      onEdit: openEditDrawer,
      onTransition: (pool, status) => void changeStatus(pool, status),
    }),
    {
      searchProps: {},
      filterProps: {
        value: collection.filters,
        onChange: filters => collection.setFilters(filters as typeof collection.filters),
        onReset: collection.resetFilters,
        filters: [
          {
            id: 'status',
            source: 'status',
            condition: FilterCondition.IN,
            type: 'dropdown',
            label: 'Status',
            multiple: true,
            options: Object.entries(RECRUITMENT_POOL_STATUS_LABELS).map(([value, label]) => ({ value, label })),
          },
        ],
      },
    },
  );

  return (
    <AddressableViewLayout
      {...binding.pageProps}
      drawer={
        <RecruitmentPoolFormDrawer
          isOpen={isFormDrawerOpen}
          onClose={() => setFormDrawerOpen(false)}
          recruitmentPool={poolToEdit}
          onSuccess={() => collection.reload()}
        />
      }
    />
  );
};

export default RecruitmentPoolsListPage;
